<template>
  <Dialog :open="true" @close="props.onClose">
    <div class="fixed inset-0 flex items-center justify-center p-4 z-20 bg-zinc-700/90">
      <DialogPanel
        class="border border-white w-[800px] h-[90vh] flex flex-col bg-black overflow-hidden"
      >
        <DialogTitle class="border-b border-white p-3 text-yellow-300">
          {{ text('ui.inn_logs_title') }}
        </DialogTitle>

        <div
          id="battle_log_body"
          class="relative w-full flex flex-col flex-1 min-h-0 overflow-hidden"
        >
          <div
            id="battle_log_labels"
            class="w-full flex text-sm font-bold p-6 pb-0 pointer-events-none"
          >
            <div class="w-[35%] text-center">{{ text('ui.description') }}</div>
            <div class="w-[20%] text-center">{{ text('ui.result') }}</div>
            <!-- upgrade/downgrade/maintain -->
            <div class="w-[45%] text-center">{{ text('ui.rewards') }}</div>
          </div>
          <div ref="scroll_container" class="relative w-full flex-1 overflow-y-auto">
            <canvas
              id="modal_canvas"
              ref="modal_canvas"
              :style="`height: ${state.canvas_height}px`"
              class="absolute top-0 left-0 w-full pointer-events-auto"
              style="z-index: 0"
            ></canvas>
            <div class="pb-6 relative pointer-events-none no-select" style="z-index: 1">
              <div
                v-if="state.pending_inn_logs_fetch"
                class="w-full pt-48 flex justify-center items-center"
              >
                <DTSpinner />
              </div>
              <div
                v-else-if="state.sorted_inn_log_entries.length === 0"
                class="mt-48 w-full text-center text-neutral-500 italic text-sm"
              >
                {{ text('ui.no_log_entries') }}
              </div>
              <div
                v-for="(log_entry, i) of state.sorted_inn_log_entries"
                v-else
                :id="`inn_log_entry_${log_entry._id || log_entry.logId}`"
                :key="log_entry._id || log_entry.logId"
                class="w-full flex text-sm p-4 items-center max-h-[64px] pointer-events-none"
              >
                <div class="w-[35%] text-center pointer-events-auto text-sm text-white/80">
                  {{ text('ui.get_inn_log_entry_description')(log_entry) }}
                </div>
                <div class="w-[25%] pointer-events-auto">
                  <!-- {{ text(`ui.game_modes.${log_entry.game_mode}`) }}  -->
                  <div
                    v-if="log_entry.new_tier > log_entry.old_tier"
                    class="w-full text-center text-green-500"
                  >
                    {{ text('ui.tier_up') }}
                  </div>
                  <div
                    v-else-if="log_entry.new_tier < log_entry.old_tier"
                    class="w-full text-center text-red-500"
                  >
                    {{ text('ui.tier_down') }}
                  </div>
                  <div v-else class="w-full text-center text-yellow-500">
                    {{ text('ui.tier_maintained') }}
                  </div>
                </div>
                <div class="w-[30%] text-center pointer-events-none">
                  <BattleRewardsIcons
                    v-if="log_entry.rewards && state.modal_pixi_app"
                    :key="`${state.rewards_key}_${i}`"
                    :inn_log_id="log_entry._id || log_entry.logId"
                    :modal_pixi_stage="state.modal_pixi_app.stage"
                    :rewards="log_entry.rewards"
                    class="mr-3"
                  />
                </div>
                <div
                  :class="{
                    'w-[5%] p-3 flex justify-center items-center': true,
                    invisible: claimAllBtnInvisibility(log_entry),
                  }"
                >
                  <div
                    class="text-center text-sm text-blue-500 hover:text-yellow-300 cursor-pointer pointer-events-auto leading-none"
                    @click="onClaimAllClick(log_entry._id || log_entry.logId, $event)"
                  >
                    {{ text('ui.claim_all') }}
                  </div>
                </div>
                <div class="w-[5%] flex justify-center items-center">
                  <div
                    :class="{
                      ['ml-6 p-3 text-lg font-bold text-red-800 hover:text-yellow-300 cursor-pointer']: true,
                      ['pointer-events-auto']: !state.pending_claim_all[log_entry._id],
                      ['pointer-events-none']: state.pending_claim_all[log_entry._id],
                    }"
                    @click="deleteLogEntry(log_entry._id || log_entry.logId)"
                  >
                    <DTAnimatedElipsis
                      v-if="state.pending_claim_all[log_entry._id || log_entry.logId]"
                      class="text-tiny font-extrabold"
                    />
                    <span v-else>{{ 'x' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogPanel>
    </div>
  </Dialog>
</template>

<script setup>
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/vue';
import { nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { BattleDebriefActions, InnActions } from '~/flux/actions';
import {
  CurrencyStore,
  GameStateStore,
  InnStore,
  NotificationsStore,
  PlayerStore,
} from '~/flux/stores';
import text from '~/text';
import CanvasTools from '~/view/CanvasTools';
import { DTAnimatedElipsis, DTSpinner } from '~/view/components/common/DOM';
import BattleRewardsIcons from './BattleRewardsIcons.vue';

const props = defineProps({
  onClose: {
    type: Function,
    required: true,
  },
});

const modal_canvas = ref(null);
const scroll_container = ref(null);

watch(modal_canvas, (val) => {
  if (val && !state.modal_pixi_app) {
    nextTick(() => {
      state.modal_pixi_app = CanvasTools.makePixiRenderer({
        canvas_id: 'modal_canvas',
        canvas_ele: val,
      });
      nextTick(() => {
        updateCanvasSize();
      });
    });
  }
});

const state = reactive({
  modal_pixi_app: null,
  rewards_key: 0,
  sorted_inn_log_entries: [],
  pending_claim_all: {},
  pending_inn_logs_fetch: true,
  canvas_height: 0,
});

onMounted(() => {
  CurrencyStore.on(CurrencyStore.GOLD_REWARD_CLAIMED, onInnLogs);
  CurrencyStore.on(CurrencyStore.PD_REWARD_CLAIMED, onInnLogs);
  GameStateStore.on(GameStateStore.ITEM_REWARD_CLAIMED, onInnLogs);
  GameStateStore.on(GameStateStore.ALL_REWARDS_CLAIMED, onAllRewardsClaimed);
  InnStore.on(InnStore.GOT_INN_LOGS, onInnLogs);

  InnActions.fetchInnLogs();
});

onBeforeUnmount(() => {
  state.modal_pixi_app?.destroy();
  CurrencyStore.removeListener(CurrencyStore.GOLD_REWARD_CLAIMED, onInnLogs);
  CurrencyStore.removeListener(CurrencyStore.PD_REWARD_CLAIMED, onInnLogs);
  GameStateStore.removeListener(GameStateStore.ITEM_REWARD_CLAIMED, onInnLogs);
  GameStateStore.removeListener(GameStateStore.ALL_REWARDS_CLAIMED, onAllRewardsClaimed);
  InnStore.removeListener(InnStore.GOT_INN_LOGS, onInnLogs);
});

function claimAllBtnInvisibility(log_entry) {
  const are_actual_items = Object.values(log_entry.rewards?.items ?? {}).filter((x) => !!x).length;
  if (!are_actual_items && !log_entry.rewards?.gold && !log_entry.rewards?.pixieDust) {
    return true;
  }
  const rewards = log_entry.rewards[PlayerStore.getAll().loggedInPlayerId] || log_entry.rewards;
  return !rewards?.gold && !rewards?.pixieDust && !Object.values(rewards?.items ?? {}).length;
}

function deleteLogEntry(log_id) {
  BattleDebriefActions.deleteInnLogEntry(log_id);
}

function onInnLogs() {
  state.sorted_inn_log_entries = Object.values(InnStore.getAll().inn_logs).sort(
    (a, b) => b.timestamp - a.timestamp
  );
  nextTick(() => {
    updateCanvasSize();
    nextTick(() => {
      state.rewards_key++;
    });
  });

  state.pending_inn_logs_fetch = false;
}

function onClaimAllClick(log_id, event) {
  event.stopPropagation();
  state.pending_claim_all[log_id] = true;
  BattleDebriefActions.claimAllRewards_Inn(log_id);
}

function updateCanvasSize() {
  if (!scroll_container.value) return;

  // Get the scrollable content height
  const contentHeight = scroll_container.value.scrollHeight;
  state.canvas_height = contentHeight;

  nextTick(() => {
    if (state.modal_pixi_app && modal_canvas.value) {
      const { width } = modal_canvas.value.getBoundingClientRect();
      state.modal_pixi_app.renderer.resize(width, contentHeight);
    }
  });
}

function onAllRewardsClaimed({ log_id }) {
  delete state.pending_claim_all[log_id];
}
</script>
